/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Fichier : chat_widget/src/widget/hooks/useChatMessages.ts
 *
 * Principe :
 * Ce hook centralise la gestion des messages et de la connexion temps réel (Socket.IO)
 * pour un chat identifié par (sessionId + userId).
 *
 * Il gère :
 * - le chargement REST initial (récupération de l'historique des messages)
 * - la connexion Socket.IO et le join de la room du chat (`chat:join`)
 * - la réception des évènements temps réel :
 *    - `assistant:message_end` (message assistant)
 *    - `chat:state` (processing/idle → pour afficher les 3 dots)
 *    - `typing:user` (indicateur de saisie côté client)
 * - les flags d'état exposés à l'UI :
 *    - `loading` (chargement historique)
 *    - `socketConnected` (socket prêt)
 *    - `assistantWorking` (assistant en cours de traitement)
 *
 * Objectif :
 * Garder `useChatPanel` léger en séparant clairement l'historique, le temps réel et
 * les états UI associés.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { API_CHAT_URL } from "../../config";
import type { Msg } from "../types/messagesTypes";
import type {
  // ChatErrorPayload,
  ChatJoinAck,
  ChatStatePayload,
  // TypingUserPayload,
} from "../types/chatTypes";
import { isBlockedUserAgent } from "../utils/isBlockedUserAgent";

export function useChatMessages(
  sessionId: string,
  userId: string,
  open: boolean,
) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("fr-FR");
  const [agentName, setAgentName] = useState("Leo");
  const socketRef = useRef<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [assistantWorking, setAssistantWorking] = useState(false);

  const chatIdRef = useRef<string | null>(null);
  const roomIdRef = useRef<string | null>(null);

  const clientContext = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        href: "",
        origin: "",
        pathname: "",
        hostname: "",
        referrer: "",
        userAgent: "",
        language: "",
        timeZone: "",
        screenWidth: 0,
        screenHeight: 0,
        viewportWidth: 0,
        viewportHeight: 0,
      };
    }

    return {
      href: window.location.href,
      origin: window.location.origin,
      pathname: window.location.pathname,
      hostname: window.location.hostname,
      referrer: document.referrer || "",
      userAgent: navigator.userAgent || "",
      language: navigator.language || "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      screenWidth: window.screen?.width || 0,
      screenHeight: window.screen?.height || 0,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (isBlockedUserAgent()) {
        console.warn("[chat] blocked load: bot or missing user-agent");
        return;
      }

      const res = await fetch(`${API_CHAT_URL}/chat/session/${sessionId}`, {
        headers: {
          "x-user-id": userId,
          "x-client-context": encodeURIComponent(JSON.stringify(clientContext)),
        },
      });

      const data = await res.json();
      if (!cancelled) {
        chatIdRef.current = data.chatId;
        setMessages(data.messages);
        setLang(data.lang);
        setAgentName(data.agentName);
        setLoading(false);
        // console.log("[chat][rest] session loaded", {
        //   sessionId,
        //   userId,
        //   count: data?.messages?.length,
        // });
      }
    }

    if (open) {
      load();
    }
    return () => {
      cancelled = true;
    };
  }, [sessionId, userId, open, clientContext]);

  useEffect(() => {
    if (isBlockedUserAgent()) {
      console.warn("[chat] blocked socket init: bot or missing user-agent");
      return;
    }
    if (!open) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = io(API_CHAT_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      auth: { sessionId, userId, clientContext },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketConnected(true);

      socket.emit(
        "chat:join",
        {
          sessionId,
          userId,
          role: "user",
          chatId: chatIdRef.current,
          clientContext,
        },
        (ack: ChatJoinAck) => {
          if (ack.ok) roomIdRef.current = ack.roomId;
        },
      );
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
      setAssistantWorking(false);
      // console.log("[chat][socket] disconnected", { reason }); reason: Socket.DisconnectReason
    });

    socket.on("connect_error", () => {
      // console.log("[chat][socket] connect_error", err.message); err: Error
    });

    socket.on("chat:joined", () => {
      // payload: ChatJoinedPayload
    });

    // socket.emit(
    //   "whoami",
    //   {},
    //   (data: { socketId: string; role: string; joinedRooms: [] }) =>
    //     console.log(data),
    // );

    socket.on("chat:error", () => {
      // console.log("[chat][socket] chat:error", payload); payload: ChatErrorPayload
    });

    // socket.on("typing:user", (payload: TypingUserPayload) => {
    //   console.log("[chat][socket] typing:user", payload);
    // });

    socket.on("chat:state", (payload: ChatStatePayload) => {
      // console.log("[chat][socket] chat:state"); // payload
      setAssistantWorking(payload.state === "processing");
    });
    // socket.on("user:message", (payload: { text: string }) => {
    //   console.log("[chat][socket] user:message", payload);
    // });

    socket.on(
      "assistant:message_end",
      (payload: { chatId: string; reply: string; at?: number }) => {
        // console.log("[chat][socket] assistant:message_end", payload);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: payload.reply,
          } as unknown as Msg,
        ]);
        setAssistantWorking(false);
      },
    );

    socket.on(
      "agent:message",
      (payload: { chatId: string; reply: string; at?: number }) => {
        // console.log("[chat][socket] assistant:message_end", payload);
        setMessages((prev) => [
          ...prev,
          {
            role: "agent",
            text: payload.reply,
          } as unknown as Msg,
        ]);
        setAssistantWorking(false);
      },
    );

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionId, userId, clientContext, chatIdRef, open]);

  return {
    messages,
    setMessages,
    loading,
    socketConnected,
    assistantWorking,
    setAssistantWorking,
    chatIdRef,
    roomIdRef,
    socketRef,
    lang,
    agentName,
  };
}
